import { defineStore } from 'pinia'
import { collection, doc, getDoc, setDoc, updateDoc, getDocs, addDoc, deleteDoc } from 'firebase/firestore'
import { useNuxtApp } from '#app'

export interface SEO_Metadata {
  id: string
  path: string
  title: string
  description: string
  canonical: string
  robots: 'index, follow' | 'noindex, nofollow'
  schema: string
  aiSummary: string
  updatedAt?: any
}

export interface Redirect {
  id: string
  source: string
  destination: string
  statusCode: 301 | 302
  active: boolean
}

export interface SEOSettings {
    companyName: string
    websiteUrl: string
    businessType: string
    country: string
    city: string
    defaultLanguage: string

    productTitleTemplate: string
    productDescriptionTemplate: string

    categoryTitleTemplate: string
    categoryDescriptionTemplate: string

    primaryKeywordTemplate: string
    secondaryKeywordTemplates: string[]

    imageAltTemplate: string
    canonicalUrlTemplate: string

    defaultTitleSuffix: string
    defaultCity: string
    defaultCountry: string
    defaultBusinessKeyword: string
    defaultCategoryKeyword: string

    searchTagsGenerationRules: string[]

    schemaSettings: string // Stored as stringified JSON for easy editing
}

const DEFAULT_SETTINGS: SEOSettings = {
    companyName: 'Alarm Supplies Qatar',
    websiteUrl: 'https://alarmsupplies.qa',
    businessType: 'Fire Alarm & Fire Protection Products',
    country: 'Qatar',
    city: 'Doha',
    defaultLanguage: 'en',

    productTitleTemplate: '{brandName} {productName} {sku} | {categoryName} Qatar | {companyName}',
    productDescriptionTemplate: 'Buy {brandName} {productName} ({sku}) in Qatar from {companyName}. QCDD compliant fire safety products available in Doha. Contact us for best price and availability.',

    categoryTitleTemplate: '{categoryName} Qatar | Fire Protection Products Doha',
    categoryDescriptionTemplate: 'Explore certified {categoryName} in Qatar. Civil Defense approved fire safety products available at competitive prices in Doha.',

    primaryKeywordTemplate: '{brandName} {productName} {sku} Qatar',
    secondaryKeywordTemplates: [
        '{sku} Qatar',
        '{sku} Doha',
        '{brandName} {sku}',
        '{productName} {sku}',
        '{productName} Doha',
        'Buy {productName} Qatar',
        '{productName} Price Qatar',
        '{productName} Supplier Qatar',
        '{categoryName} Qatar',
        'Fire Protection Products Qatar',
        'QCDD Approved Fire Safety Equipment Doha'
    ],

    imageAltTemplate: '{brandName} {productName} {sku} Fire Protection Product Qatar',
    canonicalUrlTemplate: 'https://alarmsupplies.qa/product/{slug}',

    defaultTitleSuffix: 'Qatar',
    defaultCity: 'Doha',
    defaultCountry: 'Qatar',
    defaultBusinessKeyword: 'Fire Protection Products',
    defaultCategoryKeyword: 'Fire Safety Equipment',

    searchTagsGenerationRules: [
        '{sku}',
        '{brandName} {sku}',
        '{productName} {sku}',
        '{sku} Qatar',
        '{productName}',
        '{productName} Qatar',
        '{productName} Doha',
        '{brandName}',
        '{categoryName}',
        '{categoryName} Qatar',
        'Fire Protection Products Qatar',
        'Fire Safety Equipment Qatar',
        'QCDD Approved Doha'
    ],

    schemaSettings: JSON.stringify({
        "@type": "Organization",
        "name": "Best Alarm Supplies",
        "url": "https://alarmsupplies.qa",
        "address": {
            "addressLocality": "Doha",
            "addressCountry": "Qatar"
        }
    }, null, 2)
}

export const useSeoStore = defineStore('seo', {
    state: () => ({
        settings: { ...DEFAULT_SETTINGS } as SEOSettings,
        overrides: [] as SEO_Metadata[],
        redirects: [] as Redirect[],
        loading: false,
        error: null as string | null
    }),

    actions: {
        async fetchSettings() {
            this.loading = true
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const docRef = doc($db, 'seo_settings', 'global')
                    const docSnap = await getDoc(docRef)
                    
                    if (docSnap.exists()) {
                        this.settings = { ...DEFAULT_SETTINGS, ...docSnap.data() } as SEOSettings
                    } else {
                        // Seed default settings if they don't exist
                        await setDoc(docRef, DEFAULT_SETTINGS)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch SEO settings', err)
                this.error = 'Failed to load global SEO settings'
            } finally {
                this.loading = false
            }
        },

        async saveSettings(newSettings: SEOSettings) {
            this.loading = true
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    await setDoc(doc($db, 'seo_settings', 'global'), JSON.parse(JSON.stringify(newSettings)))
                    this.settings = { ...newSettings }
                }
            } catch (err) {
                console.error('Failed to save SEO settings', err)
                throw err
            } finally {
                this.loading = false
            }
        },
        
        async fetchOverrides() {
            this.loading = true
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const snapshot = await getDocs(collection($db, 'seo_metadata'))
                    this.overrides = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as SEO_Metadata[]
                }
            } catch (err) {
                console.error('Failed to fetch SEO metadata', err)
            } finally {
                this.loading = false
            }
        },

        async addOverride(data: Partial<SEO_Metadata>) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const ref = await addDoc(collection($db, 'seo_metadata'), { ...data, updatedAt: new Date() })
                    this.overrides.push({ id: ref.id, ...data } as SEO_Metadata)
                }
            } catch (err) { console.error(err) }
        },

        async updateOverride(id: string, data: Partial<SEO_Metadata>) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const ref = doc($db, 'seo_metadata', id)
                    await updateDoc(ref, { ...data, updatedAt: new Date() })
                    const index = this.overrides.findIndex(o => o.id === id)
                    if (index !== -1) {
                        this.overrides[index] = { ...this.overrides[index], ...data } as SEO_Metadata
                    }
                }
            } catch (err) { console.error(err) }
        },

        async deleteOverride(id: string) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    await deleteDoc(doc($db, 'seo_metadata', id))
                    this.overrides = this.overrides.filter(o => o.id !== id)
                }
            } catch (err) { console.error(err) }
        }
    }
})
